import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { Link } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { fetchFinanceSummary } from "../../../api/accounting";
import { formatCompactTZS, formatTZS } from "../../../utils/format";
import CustomizerContext from "../../../_helper/Customizer";
import CredoraIcon from "../Common/CredoraIcon";
import TablePageMetricCard from "../Common/TablePageMetricCard";
import { formatApiError, runRefresh } from "../Common/credoraToast";
import AccountingReportFilters from "../Accounting/AccountingReportFilters";
import { useAccountingReportFilters } from "../Accounting/useAccountingReportFilters";
import { FINANCE_REPORT_GROUPS } from "./financeReportConfig";
import {
  baseChartOptions,
  executiveGridColor,
  executivePrimary,
  executiveSuccess,
  executiveWarning,
} from "../Dashboard/executiveChartTheme";

const HUB_TABS = [
  { id: "overview", label: "Overview" },
  { id: "statements", label: "Statements" },
  { id: "analysis", label: "Analysis" },
  { id: "treasury", label: "Treasury" },
];

const FinanceReportsHubPage = () => {
  const { layoutURL } = useContext(CustomizerContext);
  const pub = process.env.PUBLIC_URL || "";
  const filters = useAccountingReportFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");

  const reportLink = (path) => `${pub}/credora/${path}/${layoutURL}`;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchFinanceSummary(filters.reportParams());
      setData(result);
    } catch (err) {
      setError(formatApiError(err, "Failed to load finance summary."));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [filters.reportParams]);

  useEffect(() => {
    if (filters.branchId || !filters.usePeriod) {
      load();
    }
  }, [load, filters.branchId, filters.periodId, filters.dateFrom, filters.dateTo, filters.usePeriod]);

  const positionChart = useMemo(() => {
    if (!data) return null;
    return {
      options: {
        ...baseChartOptions,
        chart: { ...baseChartOptions.chart, type: "bar", height: 280 },
        colors: [executivePrimary, executiveWarning, executiveSuccess],
        xaxis: { categories: ["Assets", "Liabilities", "Equity"] },
        yaxis: { labels: { formatter: (v) => formatCompactTZS(v) } },
        plotOptions: { bar: { columnWidth: "45%", borderRadius: 4 } },
        grid: { borderColor: executiveGridColor },
      },
      series: [
        {
          name: "Amount",
          data: [Number(data.total_assets), Number(data.total_liabilities), Number(data.total_equity)],
        },
      ],
    };
  }, [data]);

  const performanceChart = useMemo(() => {
    if (!data) return null;
    return {
      options: {
        ...baseChartOptions,
        chart: { ...baseChartOptions.chart, type: "bar", height: 280 },
        colors: [executiveSuccess, executiveWarning, executivePrimary],
        xaxis: { categories: ["Revenue", "Expenses", "Net income"] },
        yaxis: { labels: { formatter: (v) => formatCompactTZS(v) } },
        plotOptions: { bar: { columnWidth: "50%", borderRadius: 4 } },
        grid: { borderColor: executiveGridColor },
      },
      series: [
        {
          name: "P&L",
          data: [Number(data.total_revenue), Number(data.total_expense), Number(data.net_income)],
        },
      ],
    };
  }, [data]);

  const renderReportCards = (groupId) => {
    const group = FINANCE_REPORT_GROUPS.find((g) => g.id === groupId);
    if (!group) return null;
    return (
      <Row className="g-3">
        {group.reports.map((report) => (
          <Col key={report.path} md="6" lg="4">
            <Card className="h-100 border shadow-none">
              <CardBody className="d-flex flex-column">
                <div className="d-flex align-items-start gap-2 mb-2">
                  <span className="credora-icon-wrap text-primary">
                    <CredoraIcon name={report.icon} size={18} />
                  </span>
                  <div>
                    <h6 className="mb-1">{report.title}</h6>
                    <p className="text-muted small mb-0">{report.description}</p>
                  </div>
                </div>
                <Link to={reportLink(report.path)} className="btn btn-outline-primary btn-sm mt-auto align-self-start">
                  Open report
                </Link>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const periodLabel =
    data?.period_name ||
    (data?.date_from && data?.date_to ? `${data.date_from} – ${data.date_to}` : "Select filters and apply");

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Reports" parent="Reports" title="Financial Reports" />
      <Container fluid className="credora-finance-reports pb-5 mb-3">
        {error && <Alert color="danger">{error}</Alert>}

        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div>
            <h5 className="mb-1">Finance & accounting reports</h5>
            <p className="text-muted mb-0 small">
              GL statements, variance analysis, VAT, and treasury views for management and audit.
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button
              color="light"
              size="sm"
              className="border"
              onClick={() => runRefresh(load, { label: "Finance reports" })}
              disabled={loading}
            >
              Refresh
            </Button>
            <Link to={reportLink("reports/finance/export")} className="btn btn-sm btn-outline-secondary">
              Export CSV
            </Link>
          </div>
        </div>

        <AccountingReportFilters {...filters} onApply={load} loading={loading} />

        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : data ? (
          <>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <Badge color="light" className="text-dark border">
                {data.branch_name || "All branches"}
              </Badge>
              <Badge color="light" className="text-dark border">{periodLabel}</Badge>
              <Badge color={data.trial_balance_balanced ? "success" : "danger"}>
                Trial balance {data.trial_balance_balanced ? "balanced" : "out of balance"}
              </Badge>
              <Badge color={data.balance_sheet_balanced ? "success" : "warning"}>
                Balance sheet {data.balance_sheet_balanced ? "balanced" : "review"}
              </Badge>
            </div>

            <Nav pills className="nav-pills nav-primary mb-3 flex-wrap">
              {HUB_TABS.map((t) => (
                <NavItem key={t.id}>
                  <NavLink className={tab === t.id ? "active" : ""} onClick={() => setTab(t.id)} style={{ cursor: "pointer" }}>
                    {t.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent activeTab={tab}>
              <TabPane tabId="overview">
                <Row className="g-3 mb-3">
                  <Col md="3" sm="6">
                    <TablePageMetricCard title="Total assets" value={formatTZS(data.total_assets)} subtitle="Balance sheet" />
                  </Col>
                  <Col md="3" sm="6">
                    <TablePageMetricCard title="Net income" value={formatTZS(data.net_income)} subtitle="Income statement" />
                  </Col>
                  <Col md="3" sm="6">
                    <TablePageMetricCard title="Closing cash" value={formatTZS(data.closing_cash)} subtitle={`Change ${formatTZS(data.net_cash_change)}`} />
                  </Col>
                  <Col md="3" sm="6">
                    <TablePageMetricCard
                      title="Payables outstanding"
                      value={formatTZS(data.payables_outstanding)}
                      subtitle={`Receivables ${formatTZS(data.receivables_outstanding)}`}
                    />
                  </Col>
                </Row>

                <Row className="g-3 mb-3">
                  <Col lg="6">
                    <Card className="h-100">
                      <CardBody>
                        <h6 className="mb-3">Financial position</h6>
                        {positionChart && <Chart options={positionChart.options} series={positionChart.series} type="bar" height={280} />}
                      </CardBody>
                    </Card>
                  </Col>
                  <Col lg="6">
                    <Card className="h-100">
                      <CardBody>
                        <h6 className="mb-3">Profit & loss</h6>
                        {performanceChart && <Chart options={performanceChart.options} series={performanceChart.series} type="bar" height={280} />}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row className="g-3">
                  <Col md="4">
                    <TablePageMetricCard
                      title="Budget variance"
                      value={data.total_variance != null ? formatTZS(data.total_variance) : "—"}
                      subtitle={
                        data.total_budget != null
                          ? `Budget ${formatTZS(data.total_budget)} · Actual ${formatTZS(data.total_actual)}`
                          : "Set budgets in Accounting"
                      }
                    />
                  </Col>
                  <Col md="4">
                    <TablePageMetricCard
                      title="VAT (period)"
                      value={data.vat_total_tax != null ? formatTZS(data.vat_total_tax) : "—"}
                      subtitle="Output/input tax summary"
                    />
                  </Col>
                  <Col md="4">
                    <TablePageMetricCard
                      title="TB accounts"
                      value={data.trial_balance_accounts}
                      subtitle={`Opening cash ${formatTZS(data.opening_cash)}`}
                    />
                  </Col>
                </Row>
              </TabPane>

              <TabPane tabId="statements">{renderReportCards("statements")}</TabPane>
              <TabPane tabId="analysis">{renderReportCards("analysis")}</TabPane>
              <TabPane tabId="treasury">{renderReportCards("treasury")}</TabPane>
            </TabContent>
          </>
        ) : (
          <Alert color="info" className="mb-0">
            Choose a branch and period, then apply filters to load the finance summary.
          </Alert>
        )}
      </Container>
    </Fragment>
  );
};

export default FinanceReportsHubPage;
